import { Component } from 'preact'
import cx from 'classnames'
import fuzzysort from 'fuzzysort'

import ActionButton from '../action-button'
import ClubSelect, { TYPE_BASIC, TYPE_PREMIUM } from './club-select'
import CollapsibleSection from './collapsible-section'
import s from './club-section.sass'
import SelectGroup, { TYPE_RADIO } from './select-group'
import { distanceInKilometers } from '../../helpers/gps'
import Map from '../map'
import InputV2 from '../input-v2'
import { IconSearch } from '../icons/search'
import { IconQuestionSolid } from '../icons/question-solid'
import { withToaster } from '../toaster'
import { CLUB_SELECTION_TYPE, purchaseFunnelClubSelection, purchaseFunnelClubType } from '../../helpers/analytics'
import { isMobile } from '../../helpers/devices'
import Modal from '../modal'
import SelectedClub from './selected-club'
import animateScrollTo from 'animated-scroll-to'

export const defaultScrollOptions = {
  verticalOffset: -(isMobile() ? 74 + 59 + 20 : 167 + 20),
  speed: 750
}

const SORT_BY_DISTANCE = 0
const SORT_BY_ALPHABETICALLY = 1
const CLUB_RENDER_LIMIT = 3

const BASIC_INFO = 'På våra basgym erbjuder vi gym- och konditionsträning. Basgymmen har generösa obemannade öppettider. Här erbjuds ett bra träningsutbud av maskiner och fria vikter, perfekt för dig som vill ha nära till träningen. \n \n Psst, gillar du gruppträning? Spana in våra premiumgym.'
const PREMIUM_INFO = 'På våra premiumgym erbjuder vi gym- och konditionsträning. Om gymmet har gruppträning ingår det i STC:s Platinum- och Premiummedlemskap. Premiumgymmen har generösa öppettider och är ofta bemannade. Gymmet har ett större träningsutbud än våra basgym. Här möts du av en trivsam reception och lounge där du kan ladda upp inför träningen, samt återhämta dig med något att äta eller dricka från den bemannade receptionen.'

class MobileClubFilterButton extends Component {
  state = { showModal: false }

  render ({
    modalTitle,
    modalContent,
    ...rest
  }, {
    showModal
  }) {
    return (
      <div className={s.mobileFilterButton}>
        <ActionButton {...rest} />
        <IconQuestionSolid className={s.icon} onClick={() => this.setState({ showModal: true })} />
        {showModal && (
          <Modal
            title={modalTitle}
            onClose={() => this.setState({ showModal: false })}
            centered
            noScroll
            fullscreen
          >
            <p>{modalContent}</p>
          </Modal>
        )}
      </div>

    )
  }
}

const DesktopClubFilterButton = withToaster(
  ({ showToast, dismissToast, toastText, ...rest }) => (
    <ActionButton {...rest} onMouseEnter={() => showToast(toastText)} onMouseLeave={() => dismissToast()} />
  )
)

export default class ClubSection extends Component {
  state = {
    userLocation: null,
    searchBy: null,
    filterBy: null,
    sortBy: SORT_BY_ALPHABETICALLY,
    clubs: this.copyClubsFromProps(),
    selectedClubId: this.props.value || parseInt(this.props.preselect) || undefined,
    selectionType: '',
    trimmedClubs: [],
    showAll: false
  }

  selectedRef = null

  componentDidMount () {
    this.trimClubs()
    this.askGeolocation()
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevProps.clubs !== this.props.clubs) {
      this.setState({ clubs: this.copyClubsFromProps() }, () => this.trimClubs())
    }
  }

  copyClubsFromProps (userLocation) {
    let clubsToCopy = this.props.clubs.filter(v => v && v.opens === null && !v.hideInSignup)

    if (clubsToCopy.length === 0) return []

    if (userLocation) {
      clubsToCopy = clubsToCopy.map((club) => ({
        ...club,
        distance: distanceInKilometers(userLocation.coords.latitude, userLocation.coords.longitude, club.location.lat, club.location.lng)
      }))
    }

    return this.props.type === 'basic'
      ? clubsToCopy.filter(club => club.type === 'basic')
      : clubsToCopy
  }

  askGeolocation () {
    navigator.geolocation.getCurrentPosition((userLocation) => this.geolocationSuccess(userLocation), (error) => this.geolocationError(error))
  }

  geolocationSuccess (userLocation) {
    const clubs = this.copyClubsFromProps(userLocation)

    this.setState({
      userLocation,
      clubs,
      trimmedClubs: this.sortBy(clubs, SORT_BY_DISTANCE),
      sortBy: SORT_BY_DISTANCE
    })
  }

  // eslint-disable-next-line handle-callback-err
  geolocationError (error) {
    const clubs = this.copyClubsFromProps()

    this.setState({
      clubs,
      trimmedClubs: this.sortBy(clubs, SORT_BY_ALPHABETICALLY),
      sortBy: SORT_BY_ALPHABETICALLY
    })
  }

  searchBy (clubs, value) {
    const hits = fuzzysort.go(value, clubs, {
      keys: [
        'name',
        'address.city',
        'address.street'
      ],
      threshold: -999,
      // Custom score function, a = keys
      scoreFn: a => Math.max(
        a[0] ? a[0].score : -1000,
        a[1] ? a[1].score - 50 : -1000,
        a[2] ? a[2].score - 75 : -1000
      )
    })

    return hits.length
      ? hits.map((hit) => hit.obj)
      : []
  }

  filterBy (clubs, value) {
    return clubs.filter((club) => club.type === value)
  }

  sortBy (clubs, value) {
    if (value === SORT_BY_DISTANCE) {
      clubs.sort((a, b) => a.distance - b.distance)
    } else {
      clubs.sort((a, b) => a.name < b.name ? -1 : 1)
    }

    return clubs
  }

  trimClubs () {
    const { clubs, searchBy, filterBy, sortBy } = this.state

    let list = [...clubs]

    if (searchBy && searchBy.length >= 2) {
      list = this.searchBy(list, searchBy)
    } else {
      list = [...list]
    }

    if (filterBy) {
      list = this.filterBy(list, filterBy)
    }

    if (!searchBy) {
      list = this.sortBy(list, sortBy)
    }

    this.setState({
      trimmedClubs: list
    })
  }

  handleSearchBy (value) {
    this.setState({
      searchBy: value
    }, () => this.trimClubs())
  }

  handleSortBy (value) {
    if (value === SORT_BY_DISTANCE && !this.state.userLocation) {
      this.askGeolocation()
    } else {
      this.setState({
        sortBy: value
      }, () => this.trimClubs())
    }
  }

  handleFilterBy (value) {
    this.setState({
      filterBy: value !== this.state.filterBy ? value : null
    }, () => this.trimClubs())

    purchaseFunnelClubType(value)
  }

  handleOnInput (selectedClubId, selectionType) {
    this.setState({
      selectedClubId,
      selectionType
    }, () => {
      if (this.selectedRef) {
        animateScrollTo(this.selectedRef.offsetTop, defaultScrollOptions)
      }
    })
  }

  handleOnProceed () {
    const club = this.state.clubs.find(club => club.id === this.state.selectedClubId)
    const type = this.state.searchBy
      ? CLUB_SELECTION_TYPE.SEARCH
      : this.state.selectionType

    purchaseFunnelClubSelection(club.id, club.name, type)
    this.props.onInput(club.id)
  }

  render ({
    isCollapsed,
    title,
    setRef,
    type
  }, {
    trimmedClubs,
    selectedClubId,
    showAll,
    searchBy,
    filterBy,
    sortBy
  }) {
    const clubsToRender = showAll
      ? trimmedClubs
      : trimmedClubs.slice(0, CLUB_RENDER_LIMIT)

    const selectedClub = trimmedClubs.find(club => club.id === selectedClubId)

    return (
      <CollapsibleSection
        ref={setRef}
        className={cx(s.clubSection, s.maxWidth)}
        isCollapsed={isCollapsed}
      >
        <div className={s.content}>
          <h2>{title}</h2>

          <div className={s.mapContainer}>
            <Map
              className={s.mapOverride}
              pins={trimmedClubs.length > 0 && trimmedClubs}
              setCenter={selectedClubId}
              onPinClicked={(club) => this.handleOnInput(club.id, CLUB_SELECTION_TYPE.MAP)}
            />
          </div>

          <div className={s.filterBar}>
            <div className={s.searchBy}>
              <InputV2
                className={s.searchInput}
                placeholder='Sök efter gym, stad eller adress'
                value={searchBy}
                onKeyUp={(e) => this.handleSearchBy(e.target.value)}
                widgetBefore={<IconSearch className={s.searchIcon} />}
              />
            </div>

            <div className={s.filterBy}>
              {isMobile()
                ? (
                  <MobileClubFilterButton
                    className={s.filterButton}
                    black
                    activated={filterBy === TYPE_BASIC}
                    label='Bas'
                    onClick={() => this.handleFilterBy(TYPE_BASIC)}
                    modalTitle='Bas'
                    modalContent={BASIC_INFO}
                  />
                )
                : (
                  <DesktopClubFilterButton
                    toasterClassName={s.toastContainer}
                    black
                    activated={filterBy === TYPE_BASIC}
                    label='Bas'
                    toastText={BASIC_INFO}
                    onClick={() => this.handleFilterBy(TYPE_BASIC)}
                    position={'left'}
                  />
                )
              }

              {isMobile()
                ? (
                  <MobileClubFilterButton
                    className={s.filterButton}
                    yellow
                    activated={filterBy === TYPE_PREMIUM}
                    label='Premium'
                    onClick={() => this.handleFilterBy(TYPE_PREMIUM)}
                    modalTitle='Premium'
                    modalContent={PREMIUM_INFO}
                  />
                )
                : (
                  <DesktopClubFilterButton
                    toasterClassName={s.toastContainer}
                    yellow
                    disabled={type === 'basic'}
                    activated={filterBy === TYPE_PREMIUM}
                    label='Premium'
                    toastText={PREMIUM_INFO}
                    onClick={() => this.handleFilterBy(TYPE_PREMIUM)}
                    position={'left'}
                  />
                )
              }
            </div>
          </div>

          <div ref={e => { this.selectedRef = e }}>
            {selectedClub && (
              <div className={s.selectedClub}>
                <SelectedClub {...selectedClub} />
                <ActionButton className={s.selectAndProceed} label='Välj och gå vidare' onClick={() => this.handleOnProceed()} />
              </div>
            )}
          </div>

          <div className={s.clubsHeader}>
            <h3>Våra gym</h3>
            <div className={s.sortBy}>
              <span className={cx(sortBy === SORT_BY_DISTANCE && s.active)} onClick={() => this.handleSortBy(SORT_BY_DISTANCE)}>Avstånd</span> | <span className={cx(sortBy === SORT_BY_ALPHABETICALLY && s.active)} onClick={() => this.handleSortBy(SORT_BY_ALPHABETICALLY)}>A - Ö</span>
            </div>
          </div>

          <div className={s.clubListContainer}>
            <SelectGroup
              className={s.clubList}
              type={TYPE_RADIO}
              onChange={(value) => this.handleOnInput(parseInt(value), CLUB_SELECTION_TYPE.LIST)}
              value={selectedClubId}
            >
              {clubsToRender.length > 0
                ? clubsToRender.map(({ id, type, ...rest }, key) => <ClubSelect key={key} value={id} clubType={type} {...rest} />)
                : <h4 className={s.noMatches}>Kunde inte hitta några matchande gym. Testa att söka på ett gym eller stad!</h4>
              }
            </SelectGroup>
            {showAll && <div className={s.gradient} />}
          </div>

          {trimmedClubs.length > 3 && (
            <div className={s.showAll}>
              <ActionButton black label={`Visa ${showAll ? 'färre' : 'alla'} gym`} onClick={() => this.setState({ showAll: !showAll })} />
            </div>
          )}
        </div>
      </CollapsibleSection>
    )
  }
}



// WEBPACK FOOTER //
// ./components/signup-flow/club-section.js